import { cva } from 'class-variance-authority'
import { AlertCircleIcon, AlertTriangleIcon, CheckIcon, InfoIcon, MegaphoneIcon, XIcon } from 'lucide-react'
import React, { forwardRef, useMemo } from 'react'

import { SolidAlertTriangleIcon } from 'slab'
import { type IconProp } from 'slab/types'

export interface BannerProps {
  children?: React.ReactNode;
  cta?: React.ReactNode;
  /**
   * An icon will automatically be rendered for all types except for `emphasis`.
   * Only specify an icon for `type="emphasis"` or if an icon other than default
   * is needed for another `type`.
   */
  icon?: IconProp;
  onDismiss?: () => void;
  title: React.ReactNode;
  type?: 'info' | 'notice' | 'success' | 'caution' | 'warning' | 'critical' | 'emphasis';
}

function iconForType (type: BannerProps['type']) {
  switch (type) {
    case 'info':
    case 'notice':
      return InfoIcon
    case 'success':
      return CheckIcon
    case 'caution':
      return AlertCircleIcon
    case 'warning':
      return AlertTriangleIcon
    case 'critical':
      return SolidAlertTriangleIcon
    case 'emphasis':
      return MegaphoneIcon
    default:
      return undefined
  }
}

const bannerClassNames = cva('flex gap-4 p-4 border rounded-lg', {
  variants: {
    type: {
      info: 'border-blue-300 bg-blue-50',
      notice: 'border-slate-300 bg-slate-50',
      success: 'border-green-300 bg-green-50',
      caution: 'border-yellow-300 bg-yellow-50',
      warning: 'border-orange-300 bg-orange-50',
      critical: 'border-red-300 bg-red-50',
      emphasis: 'border-thatch-300 bg-gradient-to-br from-thatch-800 to-thatch-600'
    },
  },
})

const titleClassNames = cva('leading-[1.25rem] body', {
  variants: {
    hasBodyCopy: {
      true: 'font-medium',
      false: 'font-normal',
    },
    type: {
      info: 'text-blue-500',
      notice: 'text-slate-500',
      success: 'text-green-500',
      caution: 'text-yellow-500',
      warning: 'text-orange-500',
      critical: 'text-red-500',
      emphasis: 'text-thatch-100',
    },
  },
})

const iconClassNames = cva('', {
  variants: {
    type: {
      info: 'text-blue-400',
      notice: 'text-slate-400',
      success: 'text-green-400',
      caution: 'text-yellow-400',
      warning: 'text-orange-400',
      critical: 'text-red-400',
      emphasis: 'text-thatch-300'
    },
  },
})

const dismissButtonClassNames = cva(
  'appearance-none rounded-md transition-all h-6 w-6 flex shrink-0 items-center justify-center outline outline-2 outline-offset-2 outline-transparent',
  {
    variants: {
      type: {
        info: null,
        notice: null,
        success: null,
        caution: null,
        warning: null,
        critical: null,
        emphasis:
          'text-thatch-200 hover:text-thatch-50 focus-visible:outline-white',
      },
    },
    compoundVariants: [
      {
        type: ['info', 'notice', 'success', 'caution', 'warning', 'critical'],
        class:
          'text-black/50 hover:text-black/80 focus-visible:outline-thatch-500',
      },
    ],
  }
)

const bodyClassNames = cva('body space-y-2', {
  variants: {
    type: {
      info: null,
      notice: null,
      success: null,
      caution: null,
      warning: null,
      critical: null,
      emphasis: 'text-white',
    },
  },
  compoundVariants: [
    {
      type: ['info', 'notice', 'success', 'caution', 'warning', 'critical'],
      class: 'text-gray-800',
    },
  ],
})

export const Banner = forwardRef<HTMLDivElement, BannerProps>(({ children, cta, icon, onDismiss, title, type = 'info' }, forwardedRef) => {
  const hasBodyCopy = !!children

  const bannerClassName = useMemo(() => bannerClassNames({ type }), [type])
  const titleClassName = useMemo(
    () => titleClassNames({ hasBodyCopy, type }),
    [type]
  )
  const iconClassName = useMemo(() => iconClassNames({ type }), [type])
  const dismissButtonClassName = useMemo(() => dismissButtonClassNames({ type }), [type])
  const bodyClassName = useMemo(() => bodyClassNames({ type }), [type])

  const Icon = icon ?? iconForType(type)

  return (
    <div className={bannerClassName} ref={forwardedRef}>
      <div className='flex gap-4 mr-auto'>
        {Icon && (
          <div className={iconClassName}>
            <Icon aria-hidden size={20} />
          </div>
        )}
        <div className='space-y-3'>
          <div className={titleClassName}>{title}</div>
          {hasBodyCopy && <div className={bodyClassName}>{children}</div>}
          {!!cta && hasBodyCopy && <div className='mt-4'>{cta}</div>}
        </div>
      </div>
      {!!cta && !hasBodyCopy && <div className='h-5 flex items-center justify-center shrink-0'>{cta}</div>}
      {!!onDismiss && (
        <div className='h-5 w-4 flex items-center justify-center'>
          {/**
            * The height and width of the div wrapping this button is
            * intentionally smaller than the button itself. The button needs a
            * large target area for accessibility, but we don't want that to
            * affect the padding and layout of the banner as a whole.
            */}
          <button
            aria-label='Dismiss'
            className={dismissButtonClassName}
            name='dismiss'
            onClick={() => {
              onDismiss?.()
            }}
          >
            <XIcon aria-hidden size={16} />
          </button>
        </div>
      )}
    </div>
  )
})
